/**
 * Hero slider
 * ---------------------------------------------------------------------------
 * Swiping is native CSS scroll-snap. This adds autoplay, dots, arrows and the
 * progress fill — plus the pausing rules a carousel needs to be usable:
 * it stops on keyboard focus, when the tab is hidden, and when the shopper
 * takes manual control.
 *
 * Fixes carried in this version:
 *   1. Focus deadlock: pausing used to trigger on ANY focusin. The track has
 *      tabindex=0 and the arrows/dots are buttons, so a single click or tap on
 *      any control focused an element inside the slider and paused it forever
 *      (focus never leaves until you click elsewhere on the page). Pause now
 *      applies only to keyboard focus (:focus-visible), which is the case
 *      WCAG actually cares about; pointer clicks are handled by the manual()
 *      back-off instead.
 *   2. Safari snap-back: scrollTo({behavior:'smooth'}) inside a
 *      scroll-snap-type:x mandatory container is unreliable in Safari — it
 *      re-snaps to the current slide mid-animation and autoplay looks frozen.
 *      Snap is now suspended on the track during a programmatic scroll and
 *      restored just after; the target is an exact snap point so restoring
 *      never causes a jump.
 *   3. Deferred slide images: slides 2+ ship data-defer-src/srcset instead of
 *      real src/srcset (see wb-hero-slider.liquid). loading="lazy" does not
 *      actually defer horizontally off-screen carousel slides — the browser's
 *      lazy threshold applies sideways too — so ~290 KB of artwork was
 *      competing with the LCP image on mobile. hydrate() swaps the real
 *      attributes in after the load event (or on first interaction /
 *      auto-advance, whichever comes first).
 */

const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

/** How long autoplay stands down after the shopper drives it manually. */
const MANUAL_RESUME_DELAY = 9000;

/** How long the smooth programmatic scroll needs before snap is restored. */
const SNAP_RESTORE_DELAY = 700;

class WbHeroSlider extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-hero-track]');
    if (!this.track) return;

    this.slides = Array.from(this.querySelectorAll('[data-hero-slide]'));
    this.dots = Array.from(this.querySelectorAll('[data-hero-dot]'));
    this.prevBtn = this.querySelector('[data-hero-prev]');
    this.nextBtn = this.querySelector('[data-hero-next]');
    this.pauseBtn = this.querySelector('[data-hero-pause]');

    this.loop = this.dataset.loop === 'true';
    this.interval = (Number(this.dataset.interval) || 6) * 1000;
    // Never autoplay for someone who asked for reduced motion.
    this.autoplayEnabled = this.dataset.autoplay === 'true' && !REDUCED_MOTION.matches;

    this.index = 0;
    this.paused = false;
    this.userStopped = false;
    this.hydrated = false;

    this.style.setProperty('--wb-hero-duration', `${this.interval}ms`);

    if (this.slides.length < 2) {
      this.prevBtn?.remove();
      this.nextBtn?.remove();
      this.hydrate();
      return;
    }

    this.observe();
    this.wireControls();
    this.wirePauseTriggers();
    this.scheduleHydration();
    this.updateUi();

    if (this.autoplayEnabled) this.start();
  }

  disconnectedCallback() {
    this.stop();
    clearTimeout(this.manualResumeTimer);
    clearTimeout(this.resumeTimer);
    clearTimeout(this.snapRestoreTimer);
    this.observer?.disconnect();
  }

  /**
   * Swap the real src/srcset into slides 2+ (they ship with data-defer-*
   * attributes so their artwork does not compete with the LCP image).
   * Idempotent; called from several triggers, first one wins.
   */
  hydrate() {
    if (this.hydrated) return;
    this.hydrated = true;

    this.querySelectorAll('[data-defer-srcset], [data-defer-src]').forEach((el) => {
      const srcset = el.getAttribute('data-defer-srcset');
      if (srcset) {
        el.setAttribute('srcset', srcset);
        el.removeAttribute('data-defer-srcset');
      }
      const src = el.getAttribute('data-defer-src');
      if (src) {
        el.setAttribute('src', src);
        el.removeAttribute('data-defer-src');
      }
    });
  }

  /**
   * Hydrate once the first paint no longer needs the bandwidth: after the
   * window load event, in an idle slot. Interaction (swipe, pointer) and any
   * navigation via goTo() hydrate immediately as a safety net, so a slide can
   * never be scrolled into view empty.
   */
  scheduleHydration() {
    const run = () => {
      const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 200));
      idle(() => this.hydrate());
    };

    if (document.readyState === 'complete') run();
    else window.addEventListener('load', run, { once: true });

    this.track.addEventListener('touchstart', () => this.hydrate(), { once: true, passive: true });
    this.track.addEventListener('pointerdown', () => this.hydrate(), { once: true });
  }

  /** Track the visible slide via IntersectionObserver, not scroll events. */
  observe() {
    if (!('IntersectionObserver' in window)) return;

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const index = Number(entry.target.dataset.index);
          if (Number.isNaN(index) || index === this.index) return;
          this.index = index;
          this.updateUi();
        });
      },
      { root: this.track, threshold: 0.6 }
    );

    this.slides.forEach((slide) => this.observer.observe(slide));
  }

  wireControls() {
    this.prevBtn?.addEventListener('click', () => this.manual(this.index - 1));
    this.nextBtn?.addEventListener('click', () => this.manual(this.index + 1));

    this.dots.forEach((dot) => {
      dot.addEventListener('click', () => this.manual(Number(dot.dataset.index)));
    });

    this.pauseBtn?.addEventListener('click', () => {
      this.userStopped = !this.userStopped;
      this.pauseBtn.setAttribute('aria-pressed', String(this.userStopped));
      this.pauseBtn.setAttribute('aria-label', this.userStopped ? 'Play slideshow' : 'Pause slideshow');
      if (this.userStopped) this.stop();
      else this.start();
    });

    // Arrow keys move the carousel when it has focus.
    this.track.setAttribute('tabindex', '0');
    this.track.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.manual(this.index + 1);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.manual(this.index - 1);
      }
    });
  }

  wirePauseTriggers() {
    // NOTE: deliberately no pointerenter/pointerleave pause.
    //
    // This hero is full-bleed and sits directly under the header, so on a
    // laptop the cursor is very often already resting on it when the page
    // loads. Pausing the whole banner on hover meant autoplay stopped
    // immediately and never resumed until the pointer left the banner —
    // which read as "the slider doesn't auto-slide".
    //
    // Pause on focus is limited to KEYBOARD focus (:focus-visible). A pointer
    // click on the track, an arrow or a dot also fires focusin, and because
    // focus then stays inside the component, an unconditional focusin pause
    // never resumed — one click anywhere on the slider killed autoplay for
    // the rest of the visit. Keyboard users still get the pause they need;
    // pointer navigation is governed by the manual() back-off below.
    //
    // WCAG 2.2.2 (Pause, Stop, Hide) is still satisfied: there is an explicit
    // pause button, prefers-reduced-motion disables autoplay outright, and
    // keyboard focus pauses it.
    this.addEventListener('focusin', (event) => {
      if (event.target instanceof HTMLElement && event.target.matches(':focus-visible')) {
        this.pause();
      }
    });
    this.addEventListener('focusout', () => {
      if (!this.contains(document.activeElement)) this.resume();
    });

    // A swipe means the shopper is driving — stop competing with them.
    this.track.addEventListener('touchstart', () => this.pause(), { passive: true });
    this.track.addEventListener('touchend', () => {
      clearTimeout(this.resumeTimer);
      this.resumeTimer = setTimeout(() => this.resume(), 4000);
    });

    // Don't burn cycles animating a tab nobody is looking at.
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.pause();
      else this.resume();
    });
  }

  /**
   * Manual navigation defers autoplay, it does not kill it.
   *
   * This used to set userStopped = true, which permanently cancelled autoplay
   * for the rest of the visit. A single arrow or dot click — including the one
   * you make to check the slider works — meant it never auto-advanced again.
   *
   * Now it backs off for a while so it isn't fighting someone who is browsing
   * the slides, then resumes on its own. The resume also clears the paused
   * flag (unless a keyboard user is still focused inside), because the click
   * that triggered manual() may have set it via focusin in older browsers —
   * without clearing it, the restarted timer ticked forever doing nothing.
   * The pause button remains the explicit, permanent stop (that is the
   * control WCAG 2.2.2 cares about).
   */
  manual(index) {
    this.stop();
    this.goTo(index);

    clearTimeout(this.manualResumeTimer);
    this.manualResumeTimer = setTimeout(() => {
      if (this.userStopped) return;

      const active = document.activeElement;
      const keyboardFocusInside =
        active instanceof HTMLElement && this.contains(active) && active.matches(':focus-visible');

      if (!keyboardFocusInside) {
        this.paused = false;
        this.start();
      }
    }, Math.max(this.interval, MANUAL_RESUME_DELAY));
  }

  goTo(index) {
    // A slide must never scroll into view without its artwork.
    this.hydrate();

    const last = this.slides.length - 1;

    if (index < 0) index = this.loop ? last : 0;
    else if (index > last) index = this.loop ? 0 : last;

    const slide = this.slides[index];
    if (!slide) return;

    const behavior = REDUCED_MOTION.matches ? 'auto' : 'smooth';

    // Safari re-snaps a mandatory-snap container during a programmatic smooth
    // scroll, cancelling the move. Suspend snap for the duration; the target
    // is an exact snap position, so restoring it never causes a jump.
    if (behavior === 'smooth') {
      this.track.style.scrollSnapType = 'none';
      clearTimeout(this.snapRestoreTimer);
      this.snapRestoreTimer = setTimeout(() => {
        this.track.style.scrollSnapType = '';
      }, SNAP_RESTORE_DELAY);
    }

    this.track.scrollTo({
      left: slide.offsetLeft - this.track.offsetLeft,
      behavior,
    });

    this.index = index;
    this.updateUi();
  }

  updateUi() {
    this.dots.forEach((dot, i) => {
      const active = i === this.index;
      dot.classList.toggle('wb-hero__dot--active', active);
      dot.setAttribute('aria-selected', String(active));

      const fill = dot.querySelector('.wb-hero__dot-fill');
      if (!fill) return;

      // Restart the progress fill only on the active dot, and only while
      // autoplay is actually running.
      if (active && this.autoplayEnabled && !this.paused && !this.userStopped) {
        fill.removeAttribute('data-progress');
        void fill.offsetWidth; // force the animation to restart
        fill.setAttribute('data-progress', 'true');
      } else {
        fill.removeAttribute('data-progress');
      }
    });

    this.slides.forEach((slide, i) => {
      // Keep off-screen slides out of the tab order and the a11y tree.
      const hidden = i !== this.index;
      slide.toggleAttribute('inert', hidden);
      slide.setAttribute('aria-hidden', String(hidden));
    });

    if (!this.loop) {
      if (this.prevBtn) this.prevBtn.disabled = this.index === 0;
      if (this.nextBtn) this.nextBtn.disabled = this.index === this.slides.length - 1;
    }
  }

  start() {
    if (!this.autoplayEnabled || this.userStopped) return;
    this.stop();
    this.timer = setInterval(() => {
      if (this.paused) return;
      this.goTo(this.index + 1);
    }, this.interval);
    this.updateUi();
  }

  stop() {
    clearInterval(this.timer);
    this.timer = null;
  }

  pause() {
    this.paused = true;
    this.updateUi();
  }

  resume() {
    if (this.userStopped) return;
    this.paused = false;
    this.updateUi();
  }
}

if (!customElements.get('wb-hero-slider')) customElements.define('wb-hero-slider', WbHeroSlider);
